import type {
  IMapping,
  ITextIcon,
  ITaxCodeObject,
  IData,
  Route,
} from "~/interfaces";
import CF from "codice-fiscale-js";
import { DateTime } from "luxon";
import data from "~/data.json";
import type { resumeClientData } from "~/interfaces";
import { resumeMapping } from "~/mappings/resumeMapping";

/**
 * Get theme data
 */
export const getThemeData = (): IData => {
  return data;
};

/**
 * Generates a dynamic icon component based on the provided ITextIcon object.
 */
export const iconComponent = (el: ITextIcon) => {
  const component = defineAsyncComponent(
    () => import(`~/components/icons/${el.icon}.vue`),
  );
  return { icon: component, text: el.text, subtext: el.subtext };
};

/**
 * Inverts an object by swapping keys and values.
 * @param {Object} obj - The object to invert.
 */
export const invertObject = (obj: {
  [key: string]: any;
}): { [key: string]: string } => {
  return Object.keys(obj).reduce(
    (acc, key) => {
      const value = obj[key];
      acc[value] = key;
      return acc;
    },
    {} as { [key: string]: string },
  );
};

/**
 * Evaluates all functions in an object and returns the result.
 * @param obj
 */
export const evaluateObjectFunctions = (obj: any) => {
  return Object.keys(obj).reduce((acc: any, key: any) => {
    acc[key] = obj[key]();
    return acc;
  }, {});
};

/**
 * Check if provided value it's a number
 * @param value
 */
export const isNumber = (value: any): boolean => {
  return typeof value === "number";
};

/**
 * Check if provided value it's a boolean
 * @param value
 */
export const isBool = (value: any): boolean => {
  return typeof value === "boolean";
};

/**
 * Check if a string contains only digits
 * @param value 
 */
export const isNumeric = (value?: string): boolean => {
  if (!value) return false;
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code < 48 || code > 57) return false;
  }
  return true;
};

/**
 *  Removes all empty values from an object.
 * @param obj
 */
export const removeEmptyValues = (obj: any) => {
  return Object.keys(obj).reduce((acc: any, key: any) => {
    if (obj[key] !== null && obj[key] !== undefined) acc[key] = obj[key];
    return acc;
  }, {});
};

/**
 * Shows the error modal.
 */
export const showModalError = () => {
  setStateValue("errorModal", true);
};

/**
 * Scroll to top
 */
export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: "smooth",
  });
};

/**
 * Validate a form submit step.
 * @param mapping
 */
export const validateStep = (mapping: IMapping): boolean => {
  const isValid = useNuxtApp().$validate(mapping)?.passes() || false;
  if (!isValid) {
    showModalError();
  }
  return !!isValid;
};

/**
 * Get user string path
 */
export const userPath = (): string => {
  const monthlyIncome = getStateValue("monthlyIncome");
  const employmentType = getStateValue("employmentType");
  const hiringType = getStateValue("hiringType");
  const employedSince = getStateValue("employedSince", "");
  const employedTo = getStateValue("employedTo", "");
  const financialProblems = getStateValue("financialProblems");
  const choosenRata = getStateValue("rate");
  const isRevolving = getStateValue("isRevolving");
  const familyIncome = getStateValue("familyIncome");

  const AGE = userAge();
  const NOW = DateTime.now();
  const REDDITO = monthlyIncome
    ? parseInt(monthlyIncome.replace(/\D/g, ""))
    : 0;
  const IS = (arr: number[]) => arr.includes(employmentType);

  const DIPENDENTE_DETERMINATO = [JOB_TYPES.DIPENDENTE].includes(employmentType) && hiringType == HIRING_TYPES.DETERMINATO;
  const DIPENDENTE_INDETERMINATO = [JOB_TYPES.DIPENDENTE].includes(employmentType) && hiringType == HIRING_TYPES.INDETERMINATO;
  const DIPENDENTE_DETERMINATO_FEEVO_PP = NOW > DateTime.fromFormat(employedSince, "MM/yyyy").plus({ months: 3 }) && NOW.plus({ months: choosenRata }) <= DateTime.fromFormat(employedTo, "MM/yyyy");
  const PROFESSIONISTA_FEEVO_PC = NOW > DateTime.fromFormat(employedSince, "MM/yyyy").plus({ months: 12 });
  const PROFESSIONISTA_FEEVO_PP = NOW > DateTime.fromFormat(employedSince, "MM/yyyy").plus({ months: 18 });
  const DIPENDENTE_INDETERMINATO_FEEVO_PC = NOW > DateTime.fromFormat(employedSince, "MM/yyyy").plus({ months: 6 });
  const DIPENDENTE_INDETERMINATO_FEEVO_PP = NOW > DateTime.fromFormat(employedSince, "MM/yyyy").plus({ months: 6 });
  const AUTONOMO_FEEVO_PC = NOW > DateTime.fromFormat(employedSince, "MM/yyyy").plus({ months: 18 }); //moment() > moment(userData.employed_since, 'MM/YYYY').add(18, 'months');
  const CREDIT_RISK = financialProblems || (isRevolving && AGE > AGE_LIMIT) || (!isRevolving && AGE + choosenRata / 12 > 80);

  //Redirect for CREDIT_RISK && FAMILY_INCOME
  const FAMILY_INCOME = () => {
    const familyIncome = getStateValue("familyIncome");
    if (familyIncome === undefined) return USER_PATHS.FAM;
    return familyIncome ? USER_PATHS.CQ : USER_PATHS.ML;
  };
  // PrestitoPersonale + PowerCredit (with financial problems | overaged) path
  if (CREDIT_RISK) {
    if (
      IS([JOB_TYPES.PENSIONATO, JOB_TYPES.DIPENDENTE]) &&
      REDDITO > REDDITO_LIMIT
    )
      return USER_PATHS.CQ;
    else if (
      IS([
        JOB_TYPES.PENSIONATO_INVALIDO,
        JOB_TYPES.STAGIONALE,
        JOB_TYPES.AUTONOMO,
        JOB_TYPES.REDDITIERE,
        JOB_TYPES.PROFESSIONISTA,
      ]) &&
      REDDITO > REDDITO_LIMIT
    )
      return FAMILY_INCOME();
    // PrestitoPersonale path
  } else if (!isRevolving) {
    if ((DIPENDENTE_INDETERMINATO && DIPENDENTE_INDETERMINATO_FEEVO_PP && REDDITO > REDDITO_LIMIT) || (DIPENDENTE_DETERMINATO && DIPENDENTE_DETERMINATO_FEEVO_PP && REDDITO > REDDITO_LIMIT)) return USER_PATHS.FE;
    else if (IS([JOB_TYPES.PENSIONATO, JOB_TYPES.REDDITIERE]) && REDDITO > REDDITO_LIMIT) return USER_PATHS.FE;
    else if (IS([JOB_TYPES.AUTONOMO, JOB_TYPES.PROFESSIONISTA]) && REDDITO > REDDITO_LIMIT && PROFESSIONISTA_FEEVO_PP) return USER_PATHS.FE;
    else if (IS([JOB_TYPES.PENSIONATO_INVALIDO, JOB_TYPES.STAGIONALE]) && REDDITO > REDDITO_LIMIT) return FAMILY_INCOME();
    // PC (Revolving) path
  } else {
    if (IS([JOB_TYPES.AUTONOMO]) && AUTONOMO_FEEVO_PC) return USER_PATHS.FE;
    else if (
      IS([JOB_TYPES.PENSIONATO, JOB_TYPES.REDDITIERE]) &&
      REDDITO > REDDITO_LIMIT
    )
      return USER_PATHS.FE;
    else if (
      IS([JOB_TYPES.PENSIONATO_INVALIDO, JOB_TYPES.STAGIONALE]) &&
      REDDITO > REDDITO_LIMIT
    )
      return FAMILY_INCOME();
    else if (
      IS([JOB_TYPES.PROFESSIONISTA]) &&
      PROFESSIONISTA_FEEVO_PC &&
      REDDITO > REDDITO_LIMIT
    )
      return USER_PATHS.FE;
    else if (
      DIPENDENTE_INDETERMINATO &&
      DIPENDENTE_INDETERMINATO_FEEVO_PC &&
      REDDITO > REDDITO_LIMIT
    )
      return USER_PATHS.FE;
  }
  return USER_PATHS.ML;
};

/**
 * Retrieve extracted fiscal code from state
 */
export const userTaxCode = (): ITaxCodeObject => {
  const taxCode = getStateValue("taxCode", "");
  try {
    const data = CF.computeInverse(taxCode) as ITaxCodeObject;
    // legacy adjustments
    data.country =
      data.birthplaceProvincia === "EE" ? data.birthplace : "ITALIA";
    data.city = data.birthplaceProvincia === "EE" ? "EE" : data.birthplace;
    return data;
  } catch (e) {
    const data = TAX_CODE;
    data.cf = taxCode;
    return data;
  }
};

/**
 *  Retrieve the user's dob based on the extraction of their fiscal code.
 */
export const userDOB = (): DateTime => {
  const { year, month, day } = userTaxCode();
  const doubleDigits = (value: number) =>
    Number(value) < 10 ? `0${value}` : value;
  return DateTime.fromFormat(
    `${year}-${doubleDigits(month ?? 1)}-${doubleDigits(day ?? 1)}`,
    "yyyy-MM-dd",
  );
};

/**
 *  Retrieve the user's age based on the extraction of their fiscal code.
 */
export const userAge = (): number => {
  return Math.floor(DateTime.local().diff(userDOB(), "years").years);
};

/**
 * Set state value and return ref
 * @param key
 * @param value
 */
export const setStateValue = (key: string, value: any) => {
  return useState<any>(key, () => value);
};

/**
 * Update state value
 * @param key
 * @param value
 */
export const updateStateValue = (key: string, value: any) => {
  useState<any>(key).value = value;
};

/**
 * Get state value
 * @param key
 * @param val
 */
export const getStateValue = (key: string, defaultValue: any = undefined) => {
  const value = useState<any>(key).value;
  return isBool(value) ? value : value || defaultValue;
};

/**
 * Log navigation steps
 * @param key
 * @param value
 */
export const logSteps = (step: string) => {
  const log = getStateValue("logSteps", []);
  log.push(step);
  updateStateValue("logSteps", log);
};

/**
 * Load dev data for testing purposes
 * @param data
 */
export const loadDevValues = (data: object) => {
  Object.keys(data).forEach((key, index) => {
    setStateValue(key, data[key as keyof typeof data]);
  });
};

/**
 * Get environment type
 */
export const env = () => {
  return process.env.NODE_ENV;
};

/**
 * Get dev environment
 */
export const isDev = () => {
  return env() === "development";
};

/**
 * Determine is WhatsApp customer service is available, according to timetables
 */
export const isWaAvailable = () => {
  const START_TIME = DateTime.now()
    .setZone("Europe/Rome")
    .set({ hour: 9, minute: 30 });
  const END_TIME = DateTime.now()
    .setZone("Europe/Rome")
    .set({ hour: 13, minute: 0 });
  const FIRST_DAY = 1;
  const LAST_DAY = 5;
  const now = DateTime.now().setZone("Europe/Rome");

  return (
    now > START_TIME &&
    now < END_TIME &&
    now.weekday >= FIRST_DAY &&
    now.weekday <= LAST_DAY
  );
};

/**
 * Append "a" tag & redirect in a new tag
 * @param url
 */
export const fallbackRedirect = (url: string) => {
  const a = document.createElement("a");
  a.classList.add("hidden");
  a.setAttribute("href", url);
  a.setAttribute("target", "_blank");
  a.click();
};

/**
 * Parse JSON and replace <a> tags with onClick event
 * @param json
 */
export function parseJsonUrls(json: any) {
  const parsedJson = JSON.stringify(json, null, 2).replace(/\\"/g, "'");

  // Regex to match anchor tags with href attributes
  const regex = /<a\b[^>]*?href='([^'">]*)'[^>]*?>.*?<\/a>/g;

  const replacedJSON = parsedJson.replace(regex, (match, href) => {
    const isAbsoluteUrl =
      href.startsWith("http://") || href.startsWith("https://");

    if (isAbsoluteUrl) {
      // Check if the target attribute exists and includes '_blank'
      const targetMatch = /target='([^'<>]*)'/g.exec(match);
      const hasTargetBlank = targetMatch && targetMatch[1] === "_blank";

      if (!hasTargetBlank) {
        // Add or modify the target attribute to ensure _blank
        return match.replace(/<a\b([^>]*?)>/, `<a$1 target='_blank'>`);
      }
    } else {
      // Handle relative URLs
      const urlMatch = /href='([^'<>\%dz]+)'/g.exec(match);
      if (urlMatch) {
        const cleanedUrl = urlMatch[1];
        const targetMatch = /target='([^'<>]*)'/g.exec(match);
        const isBlank = targetMatch && targetMatch[1] === "_blank";

        return match.replace(
          urlMatch[0],
          `href=\\"javascript:void(0)\\" onclick=\\"window.nuxtNavigateTo('${cleanedUrl}', ${isBlank})\\"`,
        );
      }
    }

    return match; // Return unchanged if no conditions are met
  });

  return JSON.parse(replacedJSON);
}

async function getThemeLocale(
  localeFile: string, 
  theme: string, 
  lang = "it"
) {
    try {
      const extJson = await import(
        `~/themes/${theme}/locales/${lang}/${localeFile}.json`
      );
      return extJson
    } catch(e) {
      return {}
    }
}

/**
 * Get locale
 *
 * Replace <a> tags attributes
 *
 * @param localeFile
 * @param key
 * @param lang
 * @param extLocales
 */
export async function getLocale(
  localeFile: string,
  key: string | null = null,
  lang: string = "it",
  extLocales: boolean = false,
) {
  const config = getThemeData();
  const theme = config.theme;

  let json = await import(`~/static/locale/${lang}/${localeFile}.json`);

  if (extLocales) {
    const extJson = await getThemeLocale(localeFile, theme, lang);
    json = {...json, ...extJson}
  }

  const parsedJSON = parseJsonUrls(json);
  return key ? parsedJSON[key] : parsedJSON;
}

/*
 * Get the base URL
 */
export function baseUrl() {
  return window.baseUrl ? window.baseUrl.split("?")[0] : "/";
}

/**
 *
 * @param routes
 * @param step
 * @param defaultRoute
 */
export function getRoute(
  routes: Route[],
  step: string,
  defaultRoute: string = "",
): Route {
  return (
    routes.find((route) => route.path === step) ??
    routes.find((route) => route.name === defaultRoute) ??
    routes[0]
  );
}

/**
 *  Merge main mapping with external mapping for a specific theme
 * @param mapping
 * @param extMapping
 */
export function mergeMapping(
  mapping: IMapping,
  extMapping: IMapping,
): IMapping {
  return {
    rules: {
      ...mapping.rules,
      ...extMapping.rules,
    },
    payload: {
      ...mapping.payload,
      ...extMapping.payload,
    },
  };
}

export const getFromLocalStorage = (key: string): string | null => {
  const val = localStorage.getItem(key)
  return val === "null" ? null : val;
}

export const numericStringToBool = (val: string | null): boolean | null => val ? !!+val : null;

export const setUserDataFromServer = (data: resumeClientData) => {
    for (const localKey in resumeMapping) {
        const val = resumeMapping[localKey as keyof typeof resumeMapping](data)
        updateStateValue(localKey, val)
    }
}

/**
 * Checks if the user qualifies for a Revolving program based on credit amount and path.
 */
export const isRevolving = (): boolean => {
  return getStateValue('isRevolving');
};

/**
 * Checks if the theme is for a bank.
 */
export const isBank = (): boolean => {
  return getStateValue('envType') === 'banche';
};
